import { isIOS } from './dom.utils'

/**
 * Safari Mobile Viewport Handler
 * Handles Safari iOS address bar show/hide and viewport changes
 */

// Constants for viewport change detection
const VIEWPORT_HEIGHT_CHANGE_THRESHOLD = 50 // Minimum height change (px) to trigger callback
const VIEWPORT_HEIGHT_CHECK_INTERVAL = 100 // Interval (ms) for checking viewport height changes
const ORIENTATION_CHANGE_TIMEOUT = 100 // Timeout (ms) to wait for orientation change to complete

export class SafariMobileViewportHandler {
  constructor(onViewportChange) {
    this.onViewportChange = onViewportChange
    this.isSetup = false
    this.viewportHeightCheckInterval = null
    this.lastViewportHeight = null
    this.orientationChangeTimeout = null
    this.boundHandleOrientationChange = this.handleOrientationChange.bind(this)
    this.boundHandleSafariScroll = this.handleSafariScroll.bind(this)
  }

  /**
   * Sets up Safari mobile specific handlers for address bar show/hide
   * Handles visual viewport API, viewport height tracking, and orientation changes
   */
  setup() {
    if (this.isSetup || !isIOS()) return
    this.isSetup = true

    // 1. Visual Viewport API (best for Safari iOS 13+)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.onViewportChange)
      window.visualViewport.addEventListener('scroll', this.onViewportChange)
    }

    // 2. Track viewport height changes to detect address bar show/hide
    this.lastViewportHeight = window.innerHeight
    this.viewportHeightCheckInterval = setInterval(() => {
      const currentHeight = window.innerHeight
      // Detect significant height change (likely address bar show/hide)
      if (Math.abs(currentHeight - this.lastViewportHeight) > VIEWPORT_HEIGHT_CHANGE_THRESHOLD) {
        this.lastViewportHeight = currentHeight
        this.onViewportChange?.()
      }
    }, VIEWPORT_HEIGHT_CHECK_INTERVAL)

    // 3. Handle orientation changes
    window.addEventListener('orientationchange', this.boundHandleOrientationChange)

    // 4. Scroll-based detection as fallback
    window.addEventListener('scroll', this.boundHandleSafariScroll, { passive: true })
  }

  /**
   * Tears down Safari mobile specific handlers
   */
  teardown() {
    if (!this.isSetup) return
    this.isSetup = false

    // Remove Visual Viewport listeners
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onViewportChange)
      window.visualViewport.removeEventListener('scroll', this.onViewportChange)
    }

    // Clear viewport height check interval
    if (this.viewportHeightCheckInterval) {
      clearInterval(this.viewportHeightCheckInterval)
      this.viewportHeightCheckInterval = null
    }

    // Clear orientation change timeout if pending
    if (this.orientationChangeTimeout) {
      clearTimeout(this.orientationChangeTimeout)
      this.orientationChangeTimeout = null
    }

    // Remove orientation change listener
    window.removeEventListener('orientationchange', this.boundHandleOrientationChange)

    // Remove scroll listener
    window.removeEventListener('scroll', this.boundHandleSafariScroll)
  }

  /**
   * Handles orientation change events
   * Waits for orientation change to complete before triggering callback
   */
  handleOrientationChange() {
    // Clear any pending timeout
    if (this.orientationChangeTimeout) {
      clearTimeout(this.orientationChangeTimeout)
    }
    // Wait for orientation change to complete
    this.orientationChangeTimeout = setTimeout(() => {
      this.onViewportChange?.()
      this.orientationChangeTimeout = null
    }, ORIENTATION_CHANGE_TIMEOUT)
  }

  /**
   * Handles scroll events on Safari mobile
   * Used as fallback to detect address bar show/hide
   */
  handleSafariScroll() {
    this.onViewportChange?.()
  }
}
